import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  GraphicEq as AudioIcon,
} from '@mui/icons-material';
import { DeploymentMarkerData } from 'src/view/Project/InteractiveMap';

import hydroOverrideData from 'src/view/components/data/hydroacousticOverrideData.json';

interface HydroDetectionItem {
  species: string;
  scientificName?: string;
  duration: string;
  imageUrl?: string | null;
  audioUrl?: string | null;
  waveform: number[];
}

interface HydroacousticDetectionsPopupProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  deployment?: DeploymentMarkerData | null;
  deploymentId?: string | null;
  totalFiles?: number;
  onGetDetails?: () => void;
  onUploadData?: () => void;
  projectMedia?: any[];
  mediaUrls?: Record<string, string>;
}

const generateWaveform = (): number[] => {
  const base = [4, 10, 16, 19, 14, 8, 17, 12, 6, 3];
  return base.map((h, i) => Math.max(2, Math.min(20, Math.round(h * 0.9 + ((i % 3) - 1)))));
};



const HydroacousticDetectionsPopup: React.FC<HydroacousticDetectionsPopupProps> = ({
  open,
  onClose,
  siteName,
  deployment,
  deploymentId,
  totalFiles,
}) => {
  const [detections, setDetections] = useState<HydroDetectionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const deploymentTitle = deployment?.deployment_name || 'Hydroacoustic Sensor Unit';
  const dId = deploymentId || deployment?.deployment_id || '';
  const rawData = hydroOverrideData[dId as keyof typeof hydroOverrideData];
  const displayTotalFiles = (rawData as any)?.totalFiles ?? totalFiles;

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudioUrl(null);
    onClose();
  };

  const handleTogglePlay = (audioUrl?: string | null) => {
    if (!audioUrl) return;

    if (playingAudioUrl === audioUrl) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioUrl(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const newAudio = new Audio(audioUrl);
    newAudio.crossOrigin = 'anonymous';
    audioRef.current = newAudio;
    setPlayingAudioUrl(audioUrl);

    newAudio.play().catch((err) => {
      console.error('Failed to play hydroacoustic audio clip:', err);
      setPlayingAudioUrl(null);
    });

    newAudio.onended = () => {
      setPlayingAudioUrl(null);
      audioRef.current = null;
    };
  };

  useEffect(() => {
    if (!open) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioUrl(null);
      return;
    }

    setLoading(true);
    const dId = deploymentId || deployment?.deployment_id || '';
    const rawData = hydroOverrideData[dId as keyof typeof hydroOverrideData];
    const dataset = (rawData as any)?.detections || [];

    const timer = setTimeout(() => {
      if (dataset && dataset.length > 0) {
        const mapped: HydroDetectionItem[] = dataset.map((item: any) => ({
          ...item,
          waveform: generateWaveform(),
        }));
        setDetections(mapped);
      }
      setLoading(false);
    }, 250);

    return () => {
      clearTimeout(timer);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [open, deploymentId, deployment?.deployment_id]);


  const normalizedSiteName = siteName || deployment?.site_name || 'Unknown Site';
  const deviceTypeStr = (deployment?.device_type || 'Hydroacoustic').toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #090E0B 0%, #030403 100%)',
          border: '1.5px solid #142218',
          borderRadius: '20px',
          color: '#fff',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
          overflow: 'hidden',
          p: 2.5,
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
        },
      }}
    >
      {/* Header bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', letterSpacing: '0.5px' }}>
              {deploymentTitle}
            </Typography>
            <Chip
              label={deviceTypeStr}
              size="small"
              sx={{
                background: 'rgba(16, 185, 129, 0.12)',
                color: '#10b981',
                fontWeight: 800,
                fontSize: '9px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                height: '20px',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.55)', fontWeight: 600 }}>
            Site: <span style={{ color: '#fff' }}>{normalizedSiteName}</span>
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255, 255, 255, 0.4)', p: 0.5, '&:hover': { color: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* Stats metrics row: Total Audio Files & Hydroacoustic Detections */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 0.5 }}>
          <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
            <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px', mb: 0.5 }}>
              TOTAL AUDIO FILES
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AudioIcon sx={{ color: '#10b981', fontSize: 14 }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>
                {displayTotalFiles !== undefined ? displayTotalFiles : '0'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
            <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px', mb: 0.5 }}>
              HYDROACOUSTIC DETECTIONS
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>
                {detections.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#10b981' }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', fontWeight: 700, letterSpacing: '0.5px' }}>
                SPECIES DETECTED
              </Typography>
            </Box>

            {/* Detections items list */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                maxHeight: '220px',
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' },
              }}
            >
              {detections.map((item, idx) => {
                const isPlaying = playingAudioUrl === item.audioUrl;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: isPlaying ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                      borderRadius: '10px',
                      p: '6px 12px',
                      border: isPlaying ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(255, 255, 255, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: isPlaying ? 'rgba(16, 185, 129, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                        borderColor: 'rgba(16, 185, 129, 0.2)',
                      },
                    }}
                  >
                    {/* Play / Pause Button */}
                    <Box
                      onClick={() => handleTogglePlay(item.audioUrl)}
                      sx={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        bgcolor: item.audioUrl ? '#10b981' : 'rgba(255, 255, 255, 0.1)',
                        color: item.audioUrl ? '#000' : 'rgba(255, 255, 255, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: item.audioUrl ? 'pointer' : 'default',
                        flexShrink: 0,
                        transition: 'transform 0.15s, background-color 0.15s',
                        '&:hover': {
                          transform: item.audioUrl ? 'scale(1.08)' : 'none',
                          bgcolor: item.audioUrl ? '#0ea572' : 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      {isPlaying ? (
                        <PauseIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <PlayIcon sx={{ fontSize: 16 }} />
                      )}
                    </Box>

                    {/* Title & sparkline Waveform */}
                    <Box sx={{ flex: 1, mx: 1.5, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontWeight: 700,
                          color: '#fff',
                          mb: 0.25,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.species}

                      </Typography>
                      {/* Mini audio bar graph */}
                      <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center', height: '12px' }}>
                        {item.waveform.map((h, barIdx) => (
                          <Box
                            key={barIdx}
                            sx={{
                              width: '2px',
                              height: isPlaying ? `${Math.min(18, Math.max(3, (h * (barIdx % 2 === 0 ? 1.3 : 0.7))))}px` : `${Math.max(3, h * 0.75)}px`,
                              bgcolor: isPlaying ? '#10b981' : 'rgba(255, 255, 255, 0.2)',
                              borderRadius: '0.5px',
                              transition: 'height 0.2s',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Duration stamp & Profile photo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                        {item.duration}
                      </Typography>
                      {item.imageUrl ? (
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.species}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          sx={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '1.5px solid rgba(255, 255, 255, 0.1)',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            bgcolor: 'rgba(16, 185, 129, 0.15)',
                            color: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 700,
                            border: '1.5px solid rgba(16, 185, 129, 0.3)',
                            flexShrink: 0,
                          }}
                        >
                          {item.species.charAt(0).toUpperCase()}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HydroacousticDetectionsPopup;
